import { Injectable, inject, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LocalSubscriptionMediaView } from "@shared/components/gallery/gallery.types";
import { Media } from "@shared/components/page-layout/page-layout.types";
import { EventMediaUpload, MediaUpload } from "@shared/db/db-domain.model";
import { AiAnalysisStatus, MediaUploadStatus } from "@shared/db/db.enum";
import { db } from "@shared/db/db.repository";
import { DbService } from "@shared/db/db.service";
import { MediaUploadStorageService } from "@shared/db/services/media-upload-storage.service";
import { ReceivedStorageItemInstancesService } from "@shared/db/services/received-storage-item-instances.service";
import { MediaTypeId } from "@shared/enums/media-type.enum";
import { StorageItemInstanceStatus } from "@shared/enums/storage-item-instance-status";
import { generateGuid } from "@shared/functions/utils.type";
import { StorageItemInstanceAnalysisStatusResponse } from "@shared/models/domain/custom-views.model";
import { SubscriptionMediaView } from "@shared/models/domain/domain-view.model";
import { StorageItemInstanceDetailView } from "@shared/models/views/storage-item-instance-view.model";
import { MediaService } from "@shared/services/media.service";
import { Observable, Subject, lastValueFrom } from "rxjs";

@Injectable({
    providedIn: 'root',
})
export class EventMediaUploadStorageService extends DbService<EventMediaUpload> {
    private mediaService = inject(MediaService);
    private mediaUploadService = inject(MediaUploadStorageService);
    private receivedStorageItemInstancesService = inject(ReceivedStorageItemInstancesService);
    private route = inject(ActivatedRoute);

    private lastUpload = new Subject<SubscriptionMediaView[]>();
    private eventUploads = new Subject<EventMediaUpload[]>();
    readonly maxRetries = 5;
    isUploading = false;
    lastUpload$: Observable<SubscriptionMediaView[]> = this.lastUpload.asObservable();
    observer$: Observable<EventMediaUpload[]> = this.eventUploads.asObservable();
    isAnalyzingStorageItem = signal<StorageItemInstanceDetailView>(null);

    constructor() {
        super(db.eventMediaUploads);
    }

    checkPendingUploadsAsync(): Promise<void> {
        if (!this.isUploading) {
            this.uploadMediaInBackgroundAsync();
        }
        return Promise.resolve();
    }

    async uploadMediaInBackgroundAsync(): Promise<void> {
        this.isUploading = true;
        const upload = await this.getNextAvailableUploadAsync();
        if (upload) {
            const mediaForm = await this.mediaUploadService.getFormDataById(upload.mediaUploadId);
            if (!mediaForm) {
                await this.discardStaleUpload(upload);
                return;
            }
            mediaForm.append('subscriptionEventId', upload.subscriptionEventId);
            mediaForm.append('storageItemInstanceId', upload.storageItemInstanceId);
            mediaForm.append('subscriptionId', upload.subscriptionId);
            mediaForm.append('projectId', upload.projectId);
            await this.update(upload);

            try {
                const aiAssistedParam = this.route?.snapshot?.queryParams?.['aiAssisted'] === 'true';
                const shouldTriggerAiAnalysis = this.isAnalyzingStorageItem()?.hasBolMedia && this.isAnalyzingStorageItem()?.hasOtherMedia 
                    && [StorageItemInstanceStatus.Pending, StorageItemInstanceStatus.Missing, StorageItemInstanceStatus.Cancelled].includes(this.isAnalyzingStorageItem().status as StorageItemInstanceStatus);
                if (aiAssistedParam && shouldTriggerAiAnalysis) {
                    this.receivedStorageItemInstancesService.storageItemAnalysisProgress$.set({
                        progress: 0,
                        instanceId: upload.storageItemInstanceId,
                        isCompleted: false
                    } as StorageItemInstanceAnalysisStatusResponse);
                }
                const response = await lastValueFrom(this.mediaService.upload(mediaForm));
                
                if (response && response?.medias?.length > 0) {
                    const uploads = response.medias.map(m => {
                        return this.getSubscriptionMediaView(upload, m);
                    })
                    this.lastUpload.next(uploads);
                    await this.mediaUploadService.delete(upload.mediaUploadId);
                    await this.delete(upload.id);
                    
                    // Check if all uploads are complete for this storage item instance
                    if (aiAssistedParam && shouldTriggerAiAnalysis) {
                        await this.checkAndUpdateAiAnalysisStatus(upload);
                    }
                }
                else {
                    throw new Error(`Failed to upload media. Response: ${JSON.stringify(response)}`);
                }
            }
            catch (error) {
                console.error(error);
                await this.increaseRetryCount(upload, error as Error);
            }
            finally {
                this.uploadMediaInBackgroundAsync();
            }
        }
        else {
            this.isUploading = false;
        }
    }

    private async discardStaleUpload(upload: EventMediaUpload): Promise<void> {
        console.error(`No media data found for upload ${upload.mediaUploadId}; discarding stale upload ${upload.id}.`);
        await this.delete(upload.id);
        this.uploadMediaInBackgroundAsync();
    }

    private getSubscriptionMediaView(upload: EventMediaUpload, m: Media): SubscriptionMediaView {
        return {
            mediaId: m.id,
            mediaType: m.mediaType,
            created: m.created,
            modified: m.modified,
            storageItemInstanceId: m.storageItemInstanceId,
            projectId: upload.projectId,
            subscriptionId: upload.subscriptionId,
            subscriptionEventId: upload.subscriptionEventId,
            description: m.description,
            url: m.url,
            fullUrl: m.fullUrl,
            fileType: m.fileType,
            title: m.title,
            userId: m.userId,
            id: m.subscriptionMediaId
        } as SubscriptionMediaView;
    }

    async getLocalSubscriptionMediaViewAsync(upload: EventMediaUpload, m: MediaUpload = null): Promise<SubscriptionMediaView> {
        m ??= await this.mediaUploadService.get(upload.mediaUploadId);
        const extension = m.fileName.split('.').pop().toLowerCase();
        const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(extension);

        return {
            mediaType: upload.mediaTypeId,
            storageItemInstanceId: upload.storageItemInstanceId,
            projectId: upload.projectId,
            subscriptionId: upload.subscriptionId,
            subscriptionEventId: upload.subscriptionEventId,
            fileType: isImage ? `image/${extension}` : 'application/octet-stream',
            title: m?.fileName,
            id: generateGuid(),
            url: `local.${extension}`,
            file: m?.fileData ? new File([m.fileData], m.fileName, { type: m.fileType }) : undefined,
        } as LocalSubscriptionMediaView;
    }

    async getNextAvailableUploadAsync(): Promise<EventMediaUpload | undefined> {
        let upload: EventMediaUpload = null;
        await db.transaction('rw', this.table, async () => {
            upload = await this.table
            .where('retries').belowOrEqual(this.maxRetries)
            .and(mediaUpload => mediaUpload.status !== MediaUploadStatus.Uploading)
            .first();
            if (!upload) return upload;

            upload.status = MediaUploadStatus.Uploading;
            await this.update(upload);
            return upload;
        });
        return upload;
    }

    async increaseRetryCount(mediaUpload: EventMediaUpload, error: Error = null): Promise<number> {
        mediaUpload.retries++;
        mediaUpload.status = MediaUploadStatus.Error;
        mediaUpload.error = error?.message ??  JSON.stringify(error);
        return await this.update(mediaUpload);
    }

    override async add(item: EventMediaUpload): Promise<string> {
        const result = await super.add(item);
        this.checkPendingUploadsAsync();
        return result;
    }

    override async addMany(items: EventMediaUpload[]): Promise<string[]> {
        const result = await super.addMany(items);
        this.checkPendingUploadsAsync();
        return result;
    }

    async getMediaUploadsBySubscriptionEventId(subscriptionEventId: string, mediaTypes: MediaTypeId[] = null): Promise<EventMediaUpload[]> {
        // eslint-disable-next-line no-useless-assignment -- keeping the default for when the filter below comes back
        mediaTypes ??= [MediaTypeId.WarehouseBillOfLading, MediaTypeId.WarehouseInstanceThumbnail]
        const result = await this.table.where('subscriptionEventId').equals(subscriptionEventId)
            //.and(m => mediaTypes.includes(m.mediaTypeId))
            .toArray();
        this.eventUploads.next(result);
        return result;
    }

    /**
     * Checks if all uploads are complete for a storage item instance and updates AI analysis status
     * @param storageItemInstanceId
     */
    private async checkAndUpdateAiAnalysisStatus(upload: EventMediaUpload): Promise<void> {
        try {
            const hasPendingUploads = await this.hasPendingUploads(upload.storageItemInstanceId);
            
            if (!hasPendingUploads) {
                const receivedStorageItems = await db.receivedStorageItemInstances
                    .where('storageItemInstanceId')
                    .equals(upload.storageItemInstanceId)
                    .toArray();

                for (const item of receivedStorageItems) {
                    if (item.status === AiAnalysisStatus.Uploading) {
                        item.status = AiAnalysisStatus.Ready;
                        item.modified = new Date();
                        await db.receivedStorageItemInstances.update(item.id, item);
                    }
                }
                await this.receivedStorageItemInstancesService.analyzeStorageItemInstance(upload.storageItemInstanceId);
            } else {
                this.receivedStorageItemInstancesService.storageItemAnalysisProgress$.set(null);
            }
        } catch (error) {
            console.error(`Error checking AI analysis status for storage item ${upload.storageItemInstanceId}:`, error);
        }
    }

    /**
     * Checks if there are pending uploads for a storage item instance
     * @param storageItemInstanceId 
     * @returns
     */
    private async hasPendingUploads(storageItemInstanceId: string): Promise<boolean> {
        const eventMediaUploads = await this.table
            .where('storageItemInstanceId')
            .equals(storageItemInstanceId)
            .toArray();

        return eventMediaUploads.some(upload => upload.status === MediaUploadStatus.Pending || upload.status === MediaUploadStatus.Uploading);
    }
}
